import React, { useEffect, useMemo, useState } from 'react'
import { Card, Tab, Nav, Button, ButtonGroup } from 'react-bootstrap'
import { Tag as TagIcon, Image as ImageIcon } from 'lucide-react'
import { AnnotationDataBoundingBox } from '../../../../types/models/annotation'
import SnippetList from '../../PredictionsReview/SnippetList'
import { useAppSelector } from '../../../../store/hooks'
import { selectSelectedProjectId } from '../../../../store/slices/projectSlice'
import { useGetProjectQuery } from '../../../../services/projectsApi'
import { ProjectAnnotationType } from '../../../../types/models/project'
import ExportSnippetsModal from '../../../modals/ExportSnippetsModal'

type RightTab = 'snippets' | 'tags'

interface Props {
  contentId: string
  activeTab: RightTab
  onSelectTab: (key: RightTab) => void
  tagsContent: React.ReactNode
  boxes: AnnotationDataBoundingBox[]
  selectedBBoxClassIndex?: number
  onSnippetSelect: (box: AnnotationDataBoundingBox) => void
  hoveredBoxIndex?: number
  onHover?: (index: number | undefined) => void
  // New: Tag selection plumbing
  onToggleTagSelection?: (tagId: string, checked: boolean) => void
  selectedTagIds?: Set<string>
}

const AnnotationRightPanel: React.FC<Props> = ({
  contentId,
  activeTab,
  onSelectTab,
  tagsContent,
  boxes,
  selectedBBoxClassIndex,
  onSnippetSelect,
  hoveredBoxIndex,
  onHover,
  onToggleTagSelection,
  selectedTagIds,
}) => {
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const annotationType: ProjectAnnotationType | undefined = project?.annotation_type

  const [selectionMode, setSelectionMode] = useState(false)
  // Manual snippet selections (checkboxes in snippets list)
  const [manualSelectedIndices, setManualSelectedIndices] = useState<number[]>([])
  const [showExportModal, setShowExportModal] = useState(false)
  const [rotateLongSideVertical, setRotateLongSideVertical] = useState<boolean>(() => {
    try {
      return localStorage.getItem('annotations.rotateSnippets') === '1'
    } catch {
      return false
    }
  })

  useEffect(() => {
    try {
      localStorage.setItem('annotations.rotateSnippets', rotateLongSideVertical ? '1' : '0')
    } catch {}
  }, [rotateLongSideVertical])

  // Capability-driven tabs per annotation type
  const allowedTabs = useMemo<RightTab[]>(() => {
    switch (annotationType) {
      case 'bounding_box':
      case 'oriented_bounding_box':
        return ['snippets', 'tags']
      case 'classification':
      case 'segmentation':
      default:
        return ['tags']
    }
  }, [annotationType])

  // Ensure the active tab is valid for current capabilities
  const effectiveActiveTab: RightTab = allowedTabs.includes(activeTab) ? activeTab : allowedTabs[0]

  useEffect(() => {
    if (activeTab !== effectiveActiveTab) {
      onSelectTab(effectiveActiveTab)
    }
  }, [activeTab, effectiveActiveTab, onSelectTab])

  const snippetsCount = boxes.length
  const tagSelectedIndices = React.useMemo(() => {
    if (!selectedTagIds || selectedTagIds.size === 0) return [] as number[]
    return boxes
      .map((b, i) => ({ b, i }))
      .filter(({ b }) => selectedTagIds.has(b.name))
      .map(({ i }) => i)
  }, [boxes, selectedTagIds])
  const selectedIndices = React.useMemo(
    () => Array.from(new Set<number>([...manualSelectedIndices, ...tagSelectedIndices])),
    [manualSelectedIndices, tagSelectedIndices]
  )
  const selectedCount = selectedIndices.length

  const toggleSelectIndex = (idx: number) => {
    setManualSelectedIndices((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    )
  }

  const handleSelectAll = () => {
    setManualSelectedIndices(boxes.map((_, i) => i))
  }

  const handleSelectNone = () => {
    setManualSelectedIndices([])
    // Clear tag-based selections as well to fully sync
    if (onToggleTagSelection && selectedTagIds && selectedTagIds.size > 0) {
      selectedTagIds.forEach((tagId) => onToggleTagSelection(tagId, false))
    }
  }

  const handleExport = () => {
    if (selectedIndices.length === 0) return
    setShowExportModal(true)
  }
  // Tabs: 'snippets' and 'tags' only; no accept/reject actions in annotations review
  return (
    <Card className="bg-dark border-0 h-100 d-flex flex-column" style={{ minHeight: 0 }}>
      <Card.Body
        className="bg-dark flex-grow-1 d-flex flex-column overflow-hidden"
        style={{ minHeight: 0 }}
      >
        {allowedTabs.includes('snippets') && (
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div className="d-flex align-items-center gap-2">
              {project?.annotation_type !== 'classification' && (
                <Button
                  variant={selectionMode ? 'outline-danger' : 'outline-primary'}
                  size="sm"
                  onClick={() => {
                    setSelectionMode((v) => {
                      const next = !v
                      // If turning selection mode off, also clear tag selections for full sync
                      if (v && onToggleTagSelection && selectedTagIds && selectedTagIds.size > 0) {
                        selectedTagIds.forEach((tagId) => onToggleTagSelection(tagId, false))
                      }
                      return next
                    })
                    setManualSelectedIndices([])
                  }}
                >
                  {selectionMode ? 'Cancel' : 'Send to Classification'}
                </Button>
              )}

              {selectionMode && (
                <ButtonGroup size="sm">
                  <Button variant="outline-secondary" onClick={handleSelectAll}>
                    All
                  </Button>
                  <Button variant="outline-secondary" onClick={handleSelectNone}>
                    None
                  </Button>
                </ButtonGroup>
              )}
            </div>
            <div className="d-flex align-items-center gap-2">
              <Button
                variant="outline-success"
                size="sm"
                disabled={!selectionMode || selectedCount === 0}
                onClick={handleExport}
              >
                Export
              </Button>
            </div>
            {selectionMode && (
              <span className="text-secondary small">{selectedCount} selected</span>
            )}
          </div>
        )}
        {allowedTabs.includes('snippets') && annotationType === 'oriented_bounding_box' && (
          <div className="d-flex justify-content-end mb-2">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="rotateSnippetsSwitch"
                checked={rotateLongSideVertical}
                onChange={(e) => setRotateLongSideVertical(e.target.checked)}
              />
              <label className="form-check-label text-light" htmlFor="rotateSnippetsSwitch">
                Rotate snippets
              </label>
            </div>
          </div>
        )}
        <Tab.Container
          activeKey={effectiveActiveTab}
          onSelect={(k) => onSelectTab(k === 'snippets' ? 'snippets' : 'tags')}
        >
          <Nav variant="pills" className="mb-2 w-100" fill>
            {allowedTabs.includes('snippets') && (
              <Nav.Item className="flex-fill">
                <Nav.Link
                  eventKey="snippets"
                  className="w-100 px-2 py-1"
                  style={
                    effectiveActiveTab === 'snippets'
                      ? {
                          background: 'linear-gradient(135deg, #9333ea 0%, #c084fc 100%)',
                          color: '#fff',
                          borderRadius: '0.5rem',
                        }
                      : { color: '#e2e8f0', borderRadius: '0.5rem' }
                  }
                >
                  <span className="d-inline-flex flex-column align-items-center gap-1 text-center">
                    <ImageIcon size={16} />
                    <span
                      className="d-inline-flex align-items-center gap-1"
                      style={{ fontSize: '0.85rem' }}
                    >
                      <span className="d-none d-lg-inline">Snippets</span>
                      <span className="badge bg-primary">{snippetsCount}</span>
                    </span>
                  </span>
                </Nav.Link>
              </Nav.Item>
            )}
            <Nav.Item className="flex-fill">
              <Nav.Link
                eventKey="tags"
                className="w-100 px-2 py-1"
                style={
                  effectiveActiveTab === 'tags'
                    ? {
                        background: 'linear-gradient(135deg, #9333ea 0%, #c084fc 100%)',
                        color: '#fff',
                        borderRadius: '0.5rem',
                      }
                    : { color: '#e2e8f0', borderRadius: '0.5rem' }
                }
              >
                <span className="d-inline-flex flex-column align-items-center gap-1 text-center">
                  <TagIcon size={16} />
                  <span
                    className="d-inline-flex align-items-center gap-1"
                    style={{ fontSize: '0.85rem' }}
                  >
                    <span className="d-none d-lg-inline">Tags</span>
                  </span>
                </span>
              </Nav.Link>
            </Nav.Item>
          </Nav>
          <Tab.Content className="flex-grow-1 overflow-auto" style={{ minHeight: 0 }}>
            <Tab.Pane eventKey="tags">
              {React.isValidElement(tagsContent)
                ? React.cloneElement(
                    tagsContent as React.ReactElement<{
                      onToggleTagSelection?: (tagId: string, checked: boolean) => void
                      selectedTagIds?: Set<string>
                      selectionMode?: boolean
                    }>,
                    {
                      onToggleTagSelection,
                      selectedTagIds,
                      selectionMode,
                    }
                  )
                : tagsContent}
            </Tab.Pane>
            {allowedTabs.includes('snippets') && (
              <Tab.Pane eventKey="snippets">
                <SnippetList
                  contentId={contentId}
                  items={boxes}
                  selectedBBoxClassIndex={selectedBBoxClassIndex}
                  showConfidence={false}
                  showAcceptButton={false}
                  showRejectButton={false}
                  hoveredBoxIndex={hoveredBoxIndex}
                  onHover={onHover}
                  onSelect={(box) => onSnippetSelect(box)}
                  selectionMode={selectionMode}
                  selectedIndices={selectedIndices}
                  onToggleSelect={toggleSelectIndex}
                  rotateLongSideVertical={
                    annotationType === 'oriented_bounding_box' && rotateLongSideVertical
                  }
                />
              </Tab.Pane>
            )}
          </Tab.Content>
        </Tab.Container>
        {showExportModal && (
          <ExportSnippetsModal
            show={showExportModal}
            onHide={() => setShowExportModal(false)}
            contentId={contentId}
            boxes={selectedIndices.map((i) => boxes[i])}
          />
        )}
      </Card.Body>
    </Card>
  )
}

export default AnnotationRightPanel
